import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { api, type AuditRow } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { Spinner, Tag } from '../components/ui/Primitives'

export default function Audit() {
  const [result, setResult] = useState('')
  const [action, setAction] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['audit', result, action],
    queryFn: () => {
      const p = new URLSearchParams({ limit: '200' })
      if (result) p.set('result', result)
      if (action) p.set('action', action)
      return api.get<{ entries: AuditRow[] }>(`/audit?${p}`)
    },
    refetchInterval: 20_000,
  })

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Auditoría</h1>
          <p className="hint mt-0.5">Quién hizo qué, cuándo y desde dónde</p>
        </div>
        <div className="flex gap-2">
          <input className="input w-44" placeholder="Filtrar acción…" value={action} onChange={(e) => setAction(e.target.value)} />
          <select className="input w-36" value={result} onChange={(e) => setResult(e.target.value)}>
            <option value="">Todo</option>
            <option value="SUCCESS">Exitosas</option>
            <option value="FAILURE">Fallidas</option>
            <option value="DENIED">Denegadas</option>
          </select>
          <a className="btn-ghost text-sm" href="/api/v1/audit/export">Exportar CSV</a>
        </div>
      </header>

      {isLoading ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Fecha</th>
                <th className="px-3 py-3 font-medium">Resultado</th>
                <th className="px-3 py-3 font-medium">Acción</th>
                <th className="px-3 py-3 font-medium">Objetivo</th>
                <th className="px-3 py-3 font-medium">Quién</th>
                <th className="px-5 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {(data?.entries ?? []).map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap px-5 py-2.5 font-mono text-xs text-slate-400">{formatDateTime(e.at)}</td>
                  <td className="px-3 py-2.5">
                    <span className={clsx(
                      'rounded px-1.5 py-0.5 text-[11px] font-medium',
                      e.result === 'SUCCESS' ? 'bg-green-500/10 text-green-400'
                        : e.result === 'DENIED' ? 'bg-red-500/10 text-red-400'
                        : 'bg-amber-500/10 text-amber-400',
                    )}>
                      {e.result.toLowerCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs">{e.action}</td>
                  <td className="max-w-xs truncate px-3 py-2.5">{e.targetLabel}</td>
                  <td className="px-3 py-2.5">
                    {e.actor} {e.viaMcp && <Tag tone="purple">MCP</Tag>}
                  </td>
                  <td className="whitespace-nowrap px-5 py-2.5 font-mono text-xs text-slate-500">{e.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data?.entries ?? []).length === 0 && <p className="hint px-5 py-8">Sin registros para ese filtro.</p>}
        </div>
      )}
    </div>
  )
}
